import React, {
  createContext,
  useCallback,
  useContext,
  useState
} from "react";
import { AuthContext } from "../AuthProvider";
import useModal from "../../hooks/useModal";
import { isNewTrack } from "../../utils/isNewTrack";
import NewTrackSubscribeModal from "../../components/NewTrackSubscribeModal";

/*
 * Single source of truth for the "New track requires a subscription"
 * download gate. Exposes two ways in:
 *
 * shouldGate(track) — pure predicate used in DownloadModal to swap
 *    its body for the subscribe pitch, so every browse / player download
 *    is covered in one place (instead of per-call-site wiring)
 * requestDownload(track) — imperative gate for download paths that don't
 *    render DownloadModal (the multitrack editor).
 * 
 * Opens the standalone modal and returns true when the caller must abort its flow
 * Returns false if the user is allowed to download — caller proceeds
 * as normal.
 */

export const NewTrackSubscribeContext = createContext({
  shouldGate: _track => false,
  requestDownload: _track => false
});

const NewTrackSubscribeProvider = ({ children }) => {
  const auth = useContext(AuthContext);
  const modal = useModal();
  const [track, setTrack] = useState(null);

  // Track is inside the "New" window AND the user lacks paid access.
  const shouldGate = useCallback(
    t => {
      if (!isNewTrack(t)) return false;
      const access = auth?.hasPaidTrackAccess?.(t);
      return !access?.granted;
    },
    [auth]
  );


  const requestDownload = useCallback(
    t => {
      if (!shouldGate(t)) return false;
      setTrack(t);
      modal.open();
      return true;
    },
    [shouldGate, modal]
  );

  return (
    <NewTrackSubscribeContext.Provider value={{ shouldGate, requestDownload }}>
      {children}
      <NewTrackSubscribeModal modalState={modal} track={track} />
    </NewTrackSubscribeContext.Provider>
  );
};

export default NewTrackSubscribeProvider;
